import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Trash2, Upload, Users, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import Header from "../components/Header";
import AdminVideos from "../components/AdminVideos";
import AdminTestimonials from "../components/AdminTestimonials";
import AdminFaces from "../components/AdminFaces";
import FaceRunStrip from "../components/FaceRunStrip";
import AdminDiagnostics from "../components/AdminDiagnostics";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const Admin = ({ user }) => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [bulkEmails, setBulkEmails] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await authFetch(`${API}/admin/allowed-emails`);
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
        setIsAdmin(true);
      } else if (response.status === 403) {
        setIsAdmin(false);
        toast.error("Admin access required");
      }
    } catch (error) {
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const addEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    try {
      const response = await authFetch(
        `${API}/admin/allowed-emails?email=${encodeURIComponent(newEmail)}&name=${encodeURIComponent(newName)}`,
        {
          method: "POST",
        }
      );

      if (response.ok) {
        toast.success("Email added");
        setNewEmail("");
        setNewName("");
        fetchEmails();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add email");
      }
    } catch (error) {
      toast.error("Failed to add email");
    }
  };

  const bulkAddEmails = async () => {
    const emailList = bulkEmails
      .split(/[\n,]/)
      .map((e) => e.trim())
      .filter((e) => e);

    if (emailList.length === 0) {
      toast.error("No emails to add");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/allowed-emails/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: emailList }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.added} emails`);
        setBulkEmails("");
        setShowBulk(false);
        fetchEmails();
      } else {
        toast.error("Failed to add emails");
      }
    } catch (error) {
      toast.error("Failed to add emails");
    }
  };

  const removeEmail = async (email) => {
    if (!confirm(`Remove ${email} from allowed list?`)) return;

    try {
      const response = await authFetch(
        `${API}/admin/allowed-emails/${encodeURIComponent(email)}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        toast.success("Email removed");
        fetchEmails();
      } else {
        toast.error("Failed to remove email");
      }
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  const filteredEmails = emails.filter(
    (e) =>
      e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.name && e.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!isAdmin && !loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="flex items-center justify-center py-20">
          <Card className="bg-card border-border max-w-md">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Admin Access Required</h2>
              <p className="text-muted-foreground mb-4">
                You don't have permission to access this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Header user={user} />
      <FaceRunStrip />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <Users className="w-8 h-8 text-primary mb-2" />
              <div className="text-3xl font-bold">{emails.length}</div>
              <p className="text-muted-foreground">Allowed Members</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground mb-1">Quick Actions</p>
                  <Button
                    size="sm"
                    onClick={() => setShowBulk(!showBulk)}
                    variant="outline"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Import
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Import */}
        {showBulk && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-8"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Bulk Import Emails</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowBulk(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="mb-2 block">
                  Paste emails (one per line or comma-separated)
                </Label>
                <Textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  placeholder="john@example.com&#10;jane@example.com&#10;..."
                  rows={6}
                  className="mb-4 bg-background"
                />
                <Button onClick={bulkAddEmails} className="bg-primary text-primary-foreground">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Emails
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Add Single Email */}
        <Card className="bg-card border-border mb-8">
          <CardHeader>
            <CardTitle>Add Member</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={addEmail} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label className="mb-2 block text-sm">Email</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="bg-background"
                  data-testid="add-email-input"
                />
              </div>
              <div className="flex-1">
                <Label className="mb-2 block text-sm">Name (optional)</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-background"
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" className="bg-primary text-primary-foreground">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Email List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Allowed Emails</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-10 bg-background"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? "No matching emails found" : "No emails added yet"}
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredEmails.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted border border-border"
                  >
                    <div>
                      <div className="font-medium">{item.email}</div>
                      {item.name && (
                        <div className="text-sm text-muted-foreground">{item.name}</div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEmail(item.email)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Face Library */}
        <div className="mt-8">
          <AdminFaces />
        </div>

        {/* Tutorial Videos */}
        <div className="mt-8">
          <AdminVideos />
        </div>

        {/* Testimonials */}
        <div className="mt-8">
          <AdminTestimonials />
        </div>

        {/* Diagnostics */}
        <div className="mt-8">
          <AdminDiagnostics />
        </div>
      </main>
    </div>
  );
};

export default Admin;
